<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity=".75"
      rounded="sm"
      :show="fetchingData || showLoading"
    >
      <b-row class="mb-4">
        <b-col md="6">
          <label for="search-state">Attach a new service</label>
          <v-select
            id="search-state"
            v-model="selectedServiceToAdd"
            :options="filterServices(availableServices)"
          >
            <template slot="option" slot-scope="option">
              <span>{{ option.serviceLabel }} - {{ option.classLabel }} </span>
            </template>
            <template slot="selected-option" slot-scope="option">
              <span>{{ option.serviceLabel }} - {{ option.classLabel }} </span>
            </template>
          </v-select>
        </b-col>
        <b-col v-if="selectedServiceToAdd" md="3" class="mt-2">
          <b-button
            variant="primary"
            @click="assignService(selectedServiceToAdd)"
          >
            Add
          </b-button>
        </b-col>
      </b-row>
      <b-table
        ref="refServiceListTable"
        primary-key="id"
        class="position-relative"
        :items="driverData.services"
        :fields="tableColumns"
      >
        <template #cell(enrollment)="data">
          <b-form-group>
            <b-form-radio-group
              v-model="data.item.chosenConstrainedValue"
              :options="[
                {
                  text: 'Enabled to All Zones',
                  value: 'open',
                },
                {
                  text: 'Restrict to Zones',
                  value: 'constrained',
                },
              ]"
            />
          </b-form-group>
          <b-row v-if="data.item.chosenConstrainedValue == 'constrained'">
            <b-col md="9">
              <v-select
                v-model="data.item.metadata.constrained_zones"
                :options="zones"
                :get-option-label="(zone) => zone.zone_id"
                multiple
              >
                <template slot="option" slot-scope="option">
                  <span>{{ option.zone_id }}</span>
                </template>
                <template slot="selected-option" slot-scope="option">
                  <span>{{ option.zone_id }}</span>
                </template>
              </v-select>
            </b-col>
          </b-row>
        </template>
        <template #cell(actions)="data">
          <b-button variant="danger" @click="removeService(data.item)">
            <span>Remove</span>
          </b-button>
          <b-button
            variant="secondary"
            class="ml-2"
            @click="updateService(data.item)"
          >
            <span>Update</span>
          </b-button>
        </template>
      </b-table>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import {
  BTable,
  BOverlay,
  BButton,
  BFormGroup,
  BFormRadioGroup,
  BCol,
  BRow,
} from "bootstrap-vue";
import { onUnmounted, ref } from "@vue/composition-api";
import { find } from "lodash";
import vSelect from "vue-select";
import store from "@/store";
import serviceStoreModule from "@/views/services/serviceStoreModule";
import zoneStoreModule from "@/views/zones/zoneStoreModule";
import useServiceList from "@/views/services/useServiceList";
import { errorNotification } from "@/utils/toast-notifications";

export default {
  components: {
    BTable,
    BOverlay,
    BButton,
    BFormGroup,
    BFormRadioGroup,
    vSelect,
    BCol,
    BRow,
  },
  props: {
    driverData: {
      type: Object,
      required: true,
    },
    fetchingData: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const SERVICE_APP_STORE_MODULE_NAME = "services";
    const ZONE_APP_STORE_MODULE_NAME = "zones";
    // Register module
    if (!store.hasModule(SERVICE_APP_STORE_MODULE_NAME))
      store.registerModule(SERVICE_APP_STORE_MODULE_NAME, serviceStoreModule);
    // Register module
    if (!store.hasModule(ZONE_APP_STORE_MODULE_NAME)) {
      store.registerModule(ZONE_APP_STORE_MODULE_NAME, zoneStoreModule);
    }
    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SERVICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(SERVICE_APP_STORE_MODULE_NAME);
      if (store.hasModule(ZONE_APP_STORE_MODULE_NAME))
        store.unregisterModule(ZONE_APP_STORE_MODULE_NAME);
    });

    const services = ref([]);
    const showLoading = ref(false);
    const zones = ref([]);
    store.dispatch("zones/fetchZones").then((res) => {
      zones.value = res;
    });

    const tableColumns = [
      { key: "name", label: "Service Name" },
      { key: "class", label: "class" },
      { key: "enrollment" },
      { key: "actions" },
    ];

    const {
      services: availableServices,
      loadingServices,
      refServiceListTable,
      refetchData,
    } = useServiceList();

    const assignService = async (service) => {
      showLoading.value = true;
      await store
        .dispatch("drivers/addService", {
          driverId: props.driverData.id,
          service,
        })
        .then(async () => {
          await refetchDriver();
          showLoading.value = false;
        })
        .catch((error) => {
          const errMessage = error?.response?.data?.error?.message || "Failed";
          errorNotification(errMessage);
          showLoading.value = false;
        });
    };

    const removeService = async (service) => {
      showLoading.value = true;
      await store
        .dispatch("drivers/removeService", {
          driverId: props.driverData.id,
          serviceId: service.id,
        })
        .then(async () => {
          await refetchDriver();
          showLoading.value = false;
        });
    };

    const refetchDriver = async () => {
      ctx.emit("refreshDriver");
    };

    const filterServices = (services) => {
      if (!services) return [];
      return services.filter((service) => {
        const found = find(props.driverData.value, {
          service: service.name,
          class: service.class,
        });
        return !found;
      });
    };

    const updateService = async (service) => {
      showLoading.value = true;
      const mappedPayload = {
        id: service.id,
        metadata: {
          open_for_all_zones: service.chosenConstrainedValue === "open",
          constrained_zones: service.metadata.constrained_zones.map(
            (zone) => zone.zone_id
          ),
        },
      };
      if (service.chosenConstrainedValue === "open") {
        mappedPayload.metadata.constrained_zones = [];
      }
      await store
        .dispatch("drivers/updateService", {
          driverId: props.driverData.id,
          service: mappedPayload,
        })
        .then(async () => {
          await refetchDriver();
          showLoading.value = false;
        });
    };

    return {
      services,
      filterServices,
      removeService,
      loadingServices,
      tableColumns,
      refServiceListTable,
      refetchData,
      availableServices,
      assignService,
      updateService,
      zones,
      find,
      showLoading,
      selectedServiceToAdd: ref(null),
    };
  },
};
</script>

<style lang="scss"></style>
